/* eslint-disable react/no-danger */
/* eslint-disable no-unused-vars */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { submit, touch } from 'redux-form';
import { Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress } from '@material-ui/core';
import _ from 'lodash';
import approvalConstraints from '../../constants/approvalConstraints';
import BudgetVoteForm from './BudgetVoteForm';
import { actions as budgetActions } from '../../actions/budgetActions';
import { actions as authActions } from '../../actions/budgetAuthActions';
import BudgetVoteFormWizard from './BudgetVoteFormWizard';
import UserInfoPanel from '../budgetAuth/UserInfoPanel';
import { useVotingSettings, useSubmitVote, useSubmitDraftVote, useVoteDetails } from '../../hooks/useBudgetVoting';
import routePaths from '../../routePaths';

class BudgetVotePage extends React.Component {
  state = {
    approvalConstraintOption: null,
    savingDraft: false,
  }

  componentDidMount() {
    // Load voting settings using React Query hook is handled in render method
  }

  VotingSettingsWrapper = () => {
    // Get budget ID from route parameter
    const budgetId = this.props.match.params.id;
    
    const { data: votingSettings, isLoading, error } = useVotingSettings(budgetId);
    const submitVoteMutation = useSubmitVote();
    const submitDraftVoteMutation = useSubmitDraftVote();
    
    // Get vote ID from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const voteId = urlParams.get('voteId');
    
    // Check if voting settings already contain vote details
    const hasVoteInSettings = votingSettings && votingSettings.vote && Object.keys(votingSettings.vote).length > 0;
    
    // Only load vote details separately if not already in voting settings and voteId is provided
    const shouldLoadVoteDetails = Boolean(!hasVoteInSettings && voteId);
    const { data: voteDetails, isLoading: isLoadingVoteDetails, error: voteDetailsError } = useVoteDetails(voteId, shouldLoadVoteDetails);
    
    // Use vote details from settings if available, otherwise from separate API call
    const finalVoteDetails = hasVoteInSettings ? votingSettings.vote : voteDetails;
    
    if (isLoading || (shouldLoadVoteDetails && isLoadingVoteDetails)) {
      return <div style={{ width: '100%', marginTop: 200, textAlign: 'center' }}><CircularProgress size={50} /></div>;
    }

    if (error || (shouldLoadVoteDetails && voteDetailsError)) {
      return (
        <div style={{ width: '100%', marginTop: 200, textAlign: 'center' }}>
          <Typography color="error">
            {error ? 'Błąd ładowania ustawień głosowania' : 'Błąd ładowania szczegółów głosu'}
          </Typography>
        </div>
      );
    }

    return this.renderContent(votingSettings, submitVoteMutation, submitDraftVoteMutation, finalVoteDetails);
  };

  createFormInitialState(votingSettings) {
    const voterIdentificationMethodId = votingSettings.voterIdentificationMethods.length === 1
        ? votingSettings.voterIdentificationMethods[0].id
        : null;

    const voter = {};
    if (voterIdentificationMethodId) {
      const identificationMethod = votingSettings.voterIdentificationMethods.find(i => i.id === voterIdentificationMethodId);

      if (identificationMethod.voterRequiredData.address) {
        if (!identificationMethod.terytEnabled
          && !identificationMethod.streetsAutocomplete
          && identificationMethod.voterRequiredData.addressRestrictedToCities
          && identificationMethod.voterRequiredData.addressRestrictedToCities.length === 1) {
          voter.city = identificationMethod.voterRequiredData.addressRestrictedToCities[0];
        } else if (!identificationMethod.terytEnabled
          && identificationMethod.streetsAutocomplete
          && identificationMethod.voterRequiredData.addressRestrictedToCities
          && identificationMethod.voterRequiredData.addressRestrictedToCities.length === 1) {
          const data = _.uniqBy(identificationMethod.streetsAutocompleteData, s => s.cityId);
          const city = data.find(c => c.cityName === identificationMethod.voterRequiredData.addressRestrictedToCities[0]);
          if (city) {
            voter.city = city.cityName;
            voter.cityId = city.cityId;
          }
        }
      }
    }

    return {
      budgetId: votingSettings.id,
      voterIdentificationMethodId,
      voter,
      votes: [],
    };
  }

  submitForm = (data, votingSettings, submitVoteMutation) => {
    if (votingSettings.approvals
      && votingSettings.approvals.filter(a => a.appliesTo !== approvalConstraints.all.value).length
      && !this.state.approvalConstraintOption)
      return;

    if (data.voter && !data.voter.address && data.voter.teryt) {
      data.voter.teryt.streetCode = null;
    }
    
    return submitVoteMutation.mutate(data);
  }

  submitDraftForm = (data, submitDraftVoteMutation, isFormValid) => {
    if (!this.props.auth.signedIn) {
      return;
    }

    // If form is not valid, trigger form submission to show validation errors
    if (!isFormValid) {
      // This will trigger validation and show error messages
      this.props.submitForm('budgetVoteForm');
      return;
    }

    this.setState({ savingDraft: true });
    
    if (data.voter && !data.voter.address && data.voter.teryt) {
      data.voter.teryt.streetCode = null;
    }
    
    submitDraftVoteMutation.mutate(data, {
      onSettled: () => {
        this.setState({ savingDraft: false });
      }
    });
  }

  confirmSubmitError = (voteError, submitVoteMutation) => {
    if (voteError != null
      && (voteError === 'alreadyVoted' || voteError.startsWith('ageVerificationFailed'))) {
      window.location.href = 'https://' + window.location.host;
    }
    else {
      submitVoteMutation.reset();
    }
  }

  renderErrorDialogContent = (votingSettings, voteError, submitVoteMutation) => {
    if (voteError === 'alreadyVoted')
      return <Typography variant="subtitle1">{votingSettings.customMessages.alreadyVoted || 'Wpisane dane zostały już wcześniej wykorzystane do głosowania w aktualnym Budżecie. W celu wyjaśnień wątpliwości prosimy o kontakt z Urzędem - dane kontaktowe dostępne są w zakładce "Kontakt" na stronie głównej portalu.'}</Typography>;
    if (voteError === 'notVerified')
      return <Typography variant="subtitle1">{votingSettings.customMessages.notVerified || 'Mieszkańcu nie zostałeś zidentyfikowany. Twój głos będzie ważny po potwierdzeniu tożsamości w Urzędzie. Być może zrobiłeś "literówkę", spróbuj ponownie. Jeżeli to nie przyniesie efektu prosimy o kontakt z Urzędem - dane kontaktowe dostępne są w zakładce "Kontakt" na stronie głównej portalu.'}</Typography>;
    if (voteError === 'phoneVerificationError')
      return <Typography variant="subtitle1">Podano nieprawidłowy kod weryfikacyjny SMS</Typography>;
    if (voteError != null && voteError.startsWith('ageVerificationFailed'))
      return <Typography variant="subtitle1">{votingSettings.customMessages.ageVerificationFailed || 'Mieszkańcu Twój głos został odrzucony ze względu na ograniczenie wieku osób uprawnionych do głosowania. Zapoznaj się z regulaminem głosowania lub skontaktuj się z Urzędem.'}</Typography>;
    if (voteError === 'general' && submitVoteMutation.error)
      return <Typography variant="subtitle1">{submitVoteMutation.error.message || 'Wystąpił problem podczas oddawania głosu.'}</Typography>;
    return <Typography variant="subtitle1">Wystąpił problem podczas oddawania głosu. Upewnij się, że wybrałeś co najmniej jeden projekt.</Typography>;
  }

  transformVoteDetailsToFormValues = (voteDetails, votingSettings) => {
    if (!voteDetails) return null;

    // Transform answers to the format expected by the form
    let votes = [];
    let applicationIds = [];

    if (voteDetails.answers && voteDetails.answers.length > 0) {
      // For different voting types, transform answers accordingly
      switch (votingSettings.type) {
        case 'multiChoice':
          applicationIds = voteDetails.answers.map(answer => answer.applicationId);
          break;
        case 'singleChoice':
          // For single choice, use the first application ID
          if (voteDetails.answers[0]) {
            return {
              budgetId: voteDetails.budgetId,
              voterIdentificationMethodId: voteDetails.voterIdentificationMethodId,
              voter: voteDetails.voter,
              applicationId: voteDetails.answers[0].applicationId
            };
          }
          break;
        case 'points':
          votes = voteDetails.answers.map(answer => ({
            applicationId: answer.applicationId,
            value: answer.value || 0
          }));
          break;
        case 'value':
          applicationIds = voteDetails.answers.map(answer => answer.applicationId);
          break;
        default:
          applicationIds = voteDetails.answers.map(answer => answer.applicationId);
      }
    }

    return {
      budgetId: voteDetails.budgetId,
      voterIdentificationMethodId: voteDetails.voterIdentificationMethodId,
      voter: voteDetails.voter,
      votes: votes.length > 0 ? votes : undefined,
      applicationIds: applicationIds.length > 0 ? applicationIds : undefined
    };
  }

  renderContent = (votingSettings, submitVoteMutation, submitDraftVoteMutation, voteDetails = null) => {
    const { unit, cms, auth, account, authActions } = this.props;
    const isVoteSuccess = submitVoteMutation.isSuccess && !submitVoteMutation.error;
    
    // Handle vote errors similar to Redux vote action
    let voteError = null;
    if (submitVoteMutation.error) {
      if (submitVoteMutation.error.isVoteError) {
        // This is a specific vote error (like alreadyVoted, notVerified, etc.)
        voteError = submitVoteMutation.error.voteErrorType;
      } else {
        // This is a general error, show the message
        voteError = 'general';
      }
    }
    
    const hasExistingVote = votingSettings && votingSettings.vote && Object.keys(votingSettings.vote).length > 0;
    
    // Check if user is logged in but voting doesn't require authentication
    const isLoggedInButVotingDoesntRequireAuth = auth.signedIn && !votingSettings.voterAuthenticationRequired;
    
    const formDisabled = this.props.preview || 
                        (votingSettings.voterAuthenticationRequired && !auth.signedIn) || 
                        hasExistingVote ||
                        isLoggedInButVotingDoesntRequireAuth;

    // Transform vote details to form format or create new form state
    const initialValues = voteDetails 
      ? this.transformVoteDetailsToFormValues(voteDetails, votingSettings)
      : this.createFormInitialState(votingSettings);

    return (
      <React.Fragment>
        <div key="top" className="row top">
          <div className="col-sm-5 d-flex align-items-end">
            {auth.signedIn ? (
              <span className="budget-menu-buttons">
                <a href={routePaths.myBudgetVotes} className="btn" aria-label="Moje głosy">
                  Moje głosy
                </a>
              </span>
            ) : null}
          </div>
          <div className="col-sm-7 header">
            <div className="budget-menu">
              <UserInfoPanel 
                auth={auth} 
                authenticationRequired={votingSettings.voterAuthenticationRequired}
                authActions={authActions} 
                account={account} 
              />
              <a href="/budzet-obywatelski" className="btn">
                <i className="fa fa-long-arrow-alt-left" /> powrót
              </a>
            </div>
          </div>
        </div>
        <div key="content" style={{ marginTop: 40 }}>
          <div className="row">
            <div className="box">
              <div className="col-sm-12 text-center">
                <h2 className="box-header">{votingSettings.name} <span>Formularz głosowania</span></h2>
              </div>
              <div className="col-sm-12">
                <Grid
                  container
                  direction="row"
                  justify="center"
                  alignItems="center"
                  spacing={2}
                >
                  {cms.budget.disabled ? null : <Grid item>
                    <a href="/budzet-obywatelski/o-budzecie" target="_blank" rel="noopener noreferrer" className="btn">Zasady głosowania</a>
                  </Grid>}
                  <Grid item>
                    <a href="/budzet-obywatelski#lista-projektow" target="_blank" rel="noopener noreferrer" className="btn">Szczegóły projektów</a>
                  </Grid>
                </Grid>
              </div>
            </div>
          </div>

          {isLoggedInButVotingDoesntRequireAuth && (
            <div className="row">
                <div className="col-sm-12">
                  <div style={{ 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeaa7', 
                    borderRadius: '4px', 
                    padding: '15px', 
                    marginBottom: '20px' 
                  }}>
                    <Typography variant="body1" style={{ color: '#856404', marginBottom: '15px' }}>
                      Głosowanie jest dostępne tylko dla niezalogowanych użytkowników. Wyloguj się, aby oddać głos.
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="secondary"
                      onClick={() => authActions.signOut()}
                      style={{ backgroundColor: '#dc3545', color: 'white' }}
                    >
                      <i className="fa fa-sign-out-alt" style={{ marginRight: '8px' }} />
                      Wyloguj się
                    </Button>
                  </div>
                </div>
            </div>
          )}

          {(votingSettings.votingGuidelines && votingSettings.votingGuidelines != '<p></p>\n') ? <div className="row">
            <div className="box">
              <div className="col-sm-12">
                <div dangerouslySetInnerHTML={{ __html: votingSettings.votingGuidelines }} />
              </div>
            </div>
          </div> : null}

          {votingSettings.districtStreetsFilters && votingSettings.districtStreetsFiltersEnabled && votingSettings.districtStreetsFilters.length
            ? <BudgetVoteFormWizard
                votingSettings={votingSettings}
                unit={unit}
                onSubmit={(data) => this.submitForm(data, votingSettings, submitVoteMutation)}
                onSaveDraft={(data, isValid) => this.submitDraftForm(data, submitDraftVoteMutation, isValid)}
                initialValues={initialValues}
                disabled={formDisabled}
                submittingForm={submitVoteMutation.isLoading}
                approvalConstraintOption={this.state.approvalConstraintOption}
                onApprovalConstraintPick={type => this.setState({ approvalConstraintOption: type })}
                actions={this.props.actions}
                savingDraft={this.state.savingDraft}
                showSaveDraftButton={auth.signedIn && !hasExistingVote && !isLoggedInButVotingDoesntRequireAuth}
              />
            : <BudgetVoteForm
                votingSettings={votingSettings}
                unit={unit}
                onSubmit={(data) => this.submitForm(data, votingSettings, submitVoteMutation)}
                onSaveDraft={(data, isValid) => this.submitDraftForm(data, submitDraftVoteMutation, isValid)}
                initialValues={initialValues}
                disabled={formDisabled}
                submittingForm={submitVoteMutation.isLoading}
                approvalConstraintOption={this.state.approvalConstraintOption}
                onApprovalConstraintPick={type => this.setState({ approvalConstraintOption: type })}
                actions={this.props.actions}
                savingDraft={this.state.savingDraft}
                showSaveDraftButton={auth.signedIn && !hasExistingVote && !isLoggedInButVotingDoesntRequireAuth}
              />}
          
          <Dialog open={isVoteSuccess} aria-labelledby="dialog-title" maxWidth="md">
            <DialogTitle id="dialog-title">{votingSettings.customMessages.successTitle || 'Wszystko OK - dziękujemy za oddany głos!'}</DialogTitle>
            <DialogContent>
              <Typography variant="subtitle1">{votingSettings.customMessages.successContent || 'Prosimy o śledzenie kolejnych etapów Budżetu zgodnie z harmonogramem i zapoznanie z wynikami po zakończeniu głosowania.'}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { window.location.href = 'https://' + window.location.host; }} color="primary" variant="contained">
                Ok
              </Button>
            </DialogActions>
          </Dialog>
          
          <Dialog open={voteError != null} aria-labelledby="dialog-title" maxWidth="md">
            <DialogTitle id="dialog-title">Uwaga</DialogTitle>
            <DialogContent>
              {this.renderErrorDialogContent(votingSettings, voteError, submitVoteMutation)}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.confirmSubmitError(voteError, submitVoteMutation)} color="primary" variant="contained">
                Ok
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </React.Fragment>
    );
  }

  render() {
    return <this.VotingSettingsWrapper />;
  }
}

BudgetVotePage.defaultProps = {
  success: false,
};

BudgetVotePage.propTypes = {
  budgetVoteForm: PropTypes.object,
  actions: PropTypes.object.isRequired,
  authActions: PropTypes.object.isRequired,
  fetching: PropTypes.bool.isRequired,
  votingSettings: PropTypes.object,
  preview: PropTypes.bool,
  cms: PropTypes.object.isRequired,
  success: PropTypes.bool,
  voteError: PropTypes.string,
  unit: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  account: PropTypes.object,
  match: PropTypes.object.isRequired,
  touchForm: PropTypes.func.isRequired,
  submitForm: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    success: state.budgetVoting.success,
    voteError: state.budgetVoting.voteError,
    fetching: state.budgetVoting.fetching,
    votingSettings: state.budgetVoting.data,
    preview: window.location.href.includes('podglad'),
    cms: state.cms,
    unit: state.unit,
    auth: state.budgetAuth,
    account: state.budgetAccount.data,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(budgetActions, dispatch),
    authActions: bindActionCreators(authActions, dispatch),
    touchForm: (form, ...fields) => dispatch(touch(form, ...fields)),
    submitForm: (form) => dispatch(submit(form)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(BudgetVotePage);



// WEBPACK FOOTER //
// ./src/components/budget/BudgetVotePage.js